/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import { AfterViewInit, Directive, ElementRef, Input, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import { LocoDetail } from './../../core/model/loco-detail.model';
import { TerminalStatus } from './../../core/model/terminal-status.model';
import { TrainData } from './../../core/model/train-data.model';
import { LmsTabsDragDropService } from './../../core/services/lms-tabs-drag-drop.service';



/**
 * @export
 * @class LmsDropZoneDirective
 * @implements {AfterViewInit}
 * @implements {OnDestroy}
 */
@Directive({
  selector: '[lmsDropZone]'
})
export class LmsDropZoneDirective implements AfterViewInit, OnDestroy {
  @Input() parentSelector: string;
  @Input() childSelector: string;
  @Input() trains: Array<TrainData>;
  @Input() locos: Array<LocoDetail>;
  @Input() allData: TerminalStatus;
  private rootElement;
  private dragImage;
  private dragEl;
  private nextEl;
  private dropTarget;
  private children: Array<any>;
  private parentContainer: any;
  private dragEndBindFn: any;
  private encodedData;
  private dragEnterBindFn: any;
  private preloadEl: HTMLElement;
  private dropEndBindFn: any;
  private selectedTrainLocos: Object = {
    trains: { list: [], identifier: [] },
    locos: { list: [], identifier: [] }
  };
  private trainSelector: any;

  /**
   * Creates an instance of LmsDropZoneDirective.
   * @param {ElementRef} _element
   * @param {LmsTabsDragDropService} lmsTabsDragDropService
   *
   * @memberOf LmsDropZoneDirective
   */
  constructor(private _element: ElementRef,
    private lmsTabsDragDropService: LmsTabsDragDropService) {
    this.rootElement = this._element.nativeElement;

    DataTransfer.prototype['setDragImageNew'] = function (image, offsetX, offsetY, eventTarget) {
      let randomDraggingClassName,
        dragStylesCSS,
        dragStylesEl,
        headEl;

      // generate a random class name that will be added to the element
      randomDraggingClassName = 'setdragimage-ie-dragging-' + Math.round(Math.random() * Math.pow(10, 5)) + '-' + Date.now();

      // prepare the rules for the random class
      dragStylesCSS = [
        '.' + randomDraggingClassName,
        '{',
        'background: url("' + image.src + '") !important;',
        'width: ' + image.width + 'px !important;',
        'height: ' + image.height + 'px !important;',
        'text-indent: -9999px !important;',
        'border: 0 !important;',
        'outline: 0 !important;',
        '}',
        '.' + randomDraggingClassName + ' * {',
        'display: none !important;',
        '}'
      ];
      // create the element and add it to the head of the page
      dragStylesEl = document.createElement('style');
      dragStylesEl.innerText = dragStylesCSS.join('');
      headEl = document.getElementsByTagName('head')[0];
      headEl.appendChild(dragStylesEl);

      eventTarget.classList.add(randomDraggingClassName);
      /* immediately after adding the class, we remove it. in this way the browser will
      have time to make a snapshot and use it just so it looks like the drag element */
      setTimeout(function () {
        // remove the styles
        headEl.removeChild(dragStylesEl);
        // remove the class
        eventTarget.classList.remove(randomDraggingClassName);
      }, 0);
    };
  }

  /**
   * @memberOf LmsDropZoneDirective
   */
  ngAfterViewInit() {
    this.sortable();
    this.dragImage = document.createElement('img');
    this.dragImage.src = 'assets/images/multi-drag.png';
    this.children = this.rootElement.querySelectorAll(`.${this.childSelector}`);
    this.parentContainer = this.findAncestor(this.rootElement, this.parentSelector);
    if (navigator.appName === 'Microsoft Internet Explorer'
      || (navigator.userAgent.match(/Trident/)
        || navigator.userAgent.match(/rv:11/))) {
      this.setDragImageIEPreload((this.dragImage));
    }
  }

  /**
   * @memberOf LmsDropZoneDirective
   */
  setDragImageIEPreload = function (image) {
    let bodyEl;

    bodyEl = document.body;

    // create the element that preloads the  image
    this.preloadEl = document.createElement('div');
    this.preloadEl.style.background = 'url("' + image.src + '")';
    this.preloadEl.style.position = 'absolute';
    this.preloadEl.style.opacity = 0.001;
    this.preloadEl.id = 'ghostDragImage';
    bodyEl.appendChild(this.preloadEl);

    // after it has been preloaded, just remove the element so it won't stay forever in the DOM
    setTimeout(() => {
      if (this.preloadE1 && this.preloadE1.parentNode) {
        bodyEl.removeChild(this.preloadEl);
      }
    }, 5000);
  };

  /**
   * @memberOf LmsDropZoneDirective
   */
  ngOnDestroy() {
    // make all children draggable
    for (const child of this.children) {
      child.draggable = false;
    };
  }

  // work on dragover action
  private onDragOver(evt) {
    if (evt.preventDefault) {
      evt.preventDefault(); // Necessary. Allows us to drop.
    }
    this.lmsTabsDragDropService.origin = 'loco';
    evt.dataTransfer.dropEffect = 'move';
    this.emitValues('over');
    return false;

  }

  /**
   * @private
   * @param {any} obj
   * @returns
   *
   * @memberOf LmsDropZoneDirective
   */
  private isElement(obj) {
    try {
      // Using W3 DOM2 (works for FF, Opera and Chrom)
      return obj instanceof HTMLElement;
    } catch (e) {
      // Browsers not supporting W3 DOM2 don't have HTMLElement and
      // an exception is thrown and we end up here. Testing some
      // properties that all elements have. (works on IE7)
      return (typeof obj === 'object') &&
        (obj.nodeType === 1) && (typeof obj.style === 'object') &&
        (typeof obj.ownerDocument === 'object');
    }
  }
  // find parent element with given clasname
  private findAncestor(el: any, cls: string) {
    const tempElement = el;
    if (el === null || (this.isElement(el) && tempElement.classList.contains(cls))) {
      return tempElement;
    } else {
      let condition = true;
      while (condition) {
        if (el === null || el === undefined) {
          condition = false;
          return el;
        } else if ((el.classList === undefined) || ((el.classList && !el.classList.contains(cls)))) {
          el = el.parentNode;
        } else {
          condition = false;
        }
      }
    }

    return el;
  }

  // work on drag end
  private onDragEnd(evt) {
    evt.stopImmediatePropagation();
    this.removeGhostImage();
    evt.preventDefault();
    document.removeEventListener('dragenter', this.dragEnterBindFn);
    document.removeEventListener('dragend', this.dragEndBindFn);
    document.removeEventListener('drop', this.dropEndBindFn);
    this.emitValues('end');
    this.dropTarget = null;
    this.dragEl = null;
  }


  // remove ghost image which we set on drag start
  private removeGhostImage() {
    if (document.getElementById('ghostDragImage')) {
      document.body.removeChild(document.getElementById('ghostDragImage'));
    }
  }

  /**
   * @private
   * @param {any} type
   *
   * @memberOf LmsDropZoneDirective
   */
  private emitValues(type) {
    const dropElementName = this.findAncestor(this.dropTarget, this.parentSelector);
    const dropChildName = this.findAncestor(this.dropTarget, this.childSelector);
    const dragElementName = this.findAncestor(this.dragEl, this.parentSelector);
    const dragChildName = this.dragEl;
    this.lmsTabsDragDropService.updateDropEndValue(type, {
      dragElement: {
        type: dragElementName ? dragElementName.getAttribute('headerName') : null,
        train: {
          index: dragElementName ? dragElementName.getAttribute('index') : null,
          identifier: dragElementName ? dragElementName.getAttribute('Identifier') : null,
          data: this.setTrainData(dragElementName)
        },
        loco: {
          index: dragChildName ? dragChildName.getAttribute('index') : 'last',
          identifier: dragChildName ? dragChildName.getAttribute('Identifier') : null,
          data: dragChildName ? this.locos[dragChildName.getAttribute('index')] : null
        }
      },
      dropElement: {
        type: dropElementName ? dropElementName.getAttribute('headerName') : null,
        train: {
          index: dropElementName ? dropElementName.getAttribute('index') : null,
          identifier: dropElementName ? dropElementName.getAttribute('Identifier') : null,
          data: this.setTrainData(dropElementName)
        },
        loco:
        {
          index: dropChildName ? dropChildName.getAttribute('index') : 'last',
          identifier: dropChildName ? dropChildName.getAttribute('Identifier') : null,
          data: dropChildName ? this.locos[dropChildName.getAttribute('index')] : null
        }
      },
      trainLocos: this.selectedTrainLocos
    }
    );
  }

  /**
   * @private
   * @param {any} element
   * @returns
   *
   * @memberOf LmsDropZoneDirective
   */
  private setTrainData(element) {
    if (element && element.getAttribute('headerName') === 'INBOUND') {
      return this.allData.terminalStatusIO.inBoundTrains[element.getAttribute('index')];
    } else if (element && element.getAttribute('headerName') === 'OUTBOUND') {
      return this.allData.terminalStatusIO.outBoundTrains[element.getAttribute('index')];
    } else {
      return {};
    }
  }

  /**
   * @memberOf LmsDropZoneDirective
   */
  sortable() {
    this.rootElement.addEventListener('dragstart', (evt) => {

      let dragElement;
      if (evt.target.classList && evt.target.classList.contains(this.childSelector)) {
        dragElement = evt.target;
      } else {
        dragElement = this.findAncestor(evt.target, this.childSelector);
      }

      if (dragElement && dragElement.getAttribute('draggable') === 'false') {
        return false;
      }

      // dragElement.classList.add('selectedDraged');

      const inBoundTrains = this.allData.terminalStatusIO.inBoundTrains;
      const outBoundTrains = this.allData.terminalStatusIO.outBoundTrains;
      const inBoundTrainsLength = inBoundTrains.length;
      const outBoundTrainsLength = outBoundTrains.length;
      // for inBoundTrains
      for (let i = 0; i < inBoundTrainsLength; i++) {
        inBoundTrains[i].clicked = false;
        inBoundTrains[i].order = 0;
      }

      // for outboundTrains
      for (let i = 0; i < outBoundTrainsLength; i++) {
        outBoundTrains[i].clicked = false;
        outBoundTrains[i].order = 0;
      }
      this.dragEl = dragElement;
      this.nextEl = dragElement.nextSibling;
      const dragLocoIdentifier = <string>dragElement.getAttribute('Identifier');
      if (this.getSelectedLocosCounter(dragLocoIdentifier) > 0) {
        this.setDragGhostImage(evt, dragLocoIdentifier);
      }


      evt.dataTransfer.effectAllowed = 'move';
      evt.dataTransfer.setData('Text', this.dragEl.textContent);

      this.dragEl.style.cursor = 'move';

      this.dragEnterBindFn = this.onDragOver.bind(this);
      this.dragEndBindFn = this.onDragEnd.bind(this);
      this.dropEndBindFn = this.dropEnd.bind(this);
      document.addEventListener('dragenter', this.dragEnterBindFn, false);
      document.addEventListener('dragend', this.dragEndBindFn, false);
      document.addEventListener('drop', this.dropEndBindFn, false);
    }, false);

  }

  dropEnd(evt) {
    if (evt) {
      evt.stopPropagation();
    }
    this.dropTarget = evt.target;
    return false;
  }

  /**
   * @private
   * @param {any} evt
   * @param {any} dragedIdentifier
   *
   * @memberOf LmsDropZoneDirective
   */
  private setDragGhostImage(evt, dragedIdentifier) {
    if (navigator.appName === 'Microsoft Internet Explorer'
      || (navigator.userAgent.match(/Trident/)
        || navigator.userAgent.match(/rv:11/))) {
      evt.dataTransfer.setDragImageNew(this.dragImage, 0, 0, evt.target);
    } else {
      evt.dataTransfer.setDragImage(this.dragImage, 0, 0);
    }
  }

  // in inbound outbound and terminallist we are getting selected Locos
  private getSelectedLocosCounter(dragedIdentifier: string) {
    const inBoundTrains = this.allData.terminalStatusIO.inBoundTrains;
    const outBoundTrains = this.allData.terminalStatusIO.outBoundTrains;
    const terminalList = this.allData.terminalStatusLocos;
    const totalTerminalListLocos = [terminalList.onTerminalList,
    terminalList.atServiceCentreList,
    terminalList.onPitList,
    terminalList.inShopList,
    terminalList.inReadyTrackList,
    terminalList.storedAtTerminalList,
    terminalList.yardAndLocalList
    ];


    const inBoundTrainsLength = inBoundTrains.length;
    const outBoundTrainsLength = outBoundTrains.length;
    let counter = 0;
    // for inBoundTrains
    for (let i = 0; i < inBoundTrainsLength; i++) {
      const locos = inBoundTrains[i].locoDetails;
      const locosLength = locos.length;
      for (let k = 0; k < locosLength; k++) {
        if (locos[k].clicked && !locos[k].inPendingList && locos[k].locomotiveIdentifier !== dragedIdentifier) {
          counter++;
        }
      }
    }

    // for outboundTrains
    for (let i = 0; i < outBoundTrainsLength; i++) {
      const locos = outBoundTrains[i].locoDetails;
      const locosLength = locos.length;
      for (let k = 0; k < locosLength; k++) {
        if (locos[k].clicked && !locos[k].inPendingList && locos[k].locomotiveIdentifier !== dragedIdentifier) {
          counter++;
        }
      }
    }

    for (let g = 0; g < totalTerminalListLocos.length; g++) {
      if (!_.isEmpty(totalTerminalListLocos[g])) {
        for (let i = 0; i < totalTerminalListLocos[g].length; i++) {
          if (totalTerminalListLocos[g][i].clicked && !totalTerminalListLocos[g][i].inPendingList && totalTerminalListLocos[g][i].locomotiveIdentifier !== dragedIdentifier) {
            counter++;
          }
        }
      }
    }

    return counter;
  }
}



